/**
 * The numbers behind Overview's figures, from the live state the console already holds: the
 * activity pulse from the event feed, the agent trend, and the recent landing runs. Pure, so
 * every figure is tested.
 */
import type { FeedEvent, FeedEventKind, Pipeline } from '@orgus/protocol';
import type { RunOutcome } from '@orgus/ui';

export interface PulseKind {
  key: string;
  label: string;
  kinds: readonly FeedEventKind[];
}

/** The pulse's series, in stacking order (bottom first). Anything else counts as "Other". */
export const PULSE_KINDS: readonly PulseKind[] = [
  { key: 'sessions', label: 'Sessions', kinds: ['session.started', 'session.ended', 'session.waiting'] },
  { key: 'agents', label: 'Agents', kinds: ['subagent.started', 'subagent.finished', 'workflow.started', 'workflow.finished'] },
  { key: 'pipelines', label: 'Pipelines', kinds: ['pipeline.item', 'job', 'checkout.changed'] },
  { key: 'other', label: 'Other', kinds: [] },
];

/** The PULSE_KINDS series an event kind counts in ("Other" when none names it). */
export function seriesOf(kind: FeedEventKind): number {
  const i = PULSE_KINDS.findIndex((k) => k.kinds.includes(kind));
  return i < 0 ? PULSE_KINDS.length - 1 : i;
}

/** The activity stream's window: one bucket per clock minute, oldest first. */
export interface MinuteWindow {
  /** One count per PULSE_KINDS series in each minute; the last is the minute `now` falls in. */
  buckets: number[][];
  /** The newest minute's number (minutes since the epoch): the stream drifts when it moves on. */
  minute: number;
  /** The first bucket the feed reaches back to; the minutes before it are unknown, not quiet. */
  from: number;
}

/**
 * The last `count` clock minutes of events by kind. The console keeps only the newest events, so
 * when the feed is full (`full`) and its oldest event falls inside the window, the minutes before
 * that one are marked unknown instead of drawn as quiet.
 */
export function minuteBuckets(events: readonly Pick<FeedEvent, 'at' | 'kind'>[], now: number, count = 60, full = false): MinuteWindow {
  const minute = Math.floor(now / 60_000);
  const first = minute - count + 1;
  const buckets = Array.from({ length: count }, () => PULSE_KINDS.map(() => 0));
  let oldest = Infinity;
  for (const e of events) {
    const m = Math.floor(e.at / 60_000);
    oldest = Math.min(oldest, m);
    if (m < first || m > minute) continue;
    buckets[m - first]![seriesOf(e.kind)]! += 1;
  }
  const from = full && Number.isFinite(oldest) ? Math.min(count - 1, Math.max(0, oldest - first)) : 0;
  return { buckets, minute, from };
}

/** Agent and workflow starts per bucket over the window, oldest first: the agents tile's trend. */
export function agentStarts(events: readonly Pick<FeedEvent, 'at' | 'kind'>[], now: number, windowMs = 60 * 60_000, count = 12): number[] {
  const out = Array.from({ length: count }, () => 0);
  const start = now - windowMs;
  const size = windowMs / count;
  for (const e of events) {
    if (e.kind !== 'subagent.started' && e.kind !== 'workflow.started') continue;
    if (e.at <= start || e.at > now) continue;
    out[Math.min(count - 1, Math.floor((e.at - start) / size))]! += 1;
  }
  return out;
}

/** Finished landing runs across every pipeline, oldest first, then what is running now. */
export function recentRuns(pipelines: readonly Pick<Pipeline, 'recent' | 'items'>[]): RunOutcome[] {
  const done = pipelines
    .flatMap((p) => p.recent)
    .filter((i) => i.state === 'passed' || i.state === 'failed' || i.state === 'cancelled')
    .sort((a, b) => (a.endedAt ?? a.startedAt ?? 0) - (b.endedAt ?? b.startedAt ?? 0))
    .map((i) => i.state as RunOutcome);
  const running = pipelines.flatMap((p) => p.items).filter((i) => i.state === 'active').length;
  return [...done, ...Array.from({ length: running }, (): RunOutcome => 'running')];
}

/** "16 passed, 4 failed, 2 running" with zero parts left out. */
export function runSummary(runs: readonly RunOutcome[]): string {
  const n = (o: RunOutcome) => runs.filter((r) => r === o).length;
  const parts = [n('passed') && `${n('passed')} passed`, n('failed') && `${n('failed')} failed`, n('running') && `${n('running')} running`].filter(Boolean);
  return parts.length ? parts.join(', ') : 'No runs yet';
}

/** Events shown at first, and how many more each Show more reveals. */
export const FEED_FIRST = 6;
export const FEED_PAGE = 20;

/**
 * How many events the list shows: the first few, then more a page at a time, up to everything in
 * the last hour (the panel's window). `rest` is how many more the hour holds.
 */
export function feedPage(hourCount: number, total: number, limit: number): { show: number; rest: number } {
  const reach = Math.max(Math.min(FEED_FIRST, total), hourCount);
  const show = Math.min(total, Math.min(limit, reach));
  return { show, rest: Math.max(0, reach - show) };
}
